import { useCallback, useEffect, useState } from "react";
import DialogNoFooter from "@/app/models/dialogs/DialogNoFooter";
import { NotificationTab } from "./notificationTab";
import { NotificationView } from "./notificationView";
import "./Notification.scss";
import { useDispatch } from "react-redux";
import { handleNotification } from "@/app/redux/reducers/userSlice";
import {
  gxnotifyupdateApi,
  gxnotifycategoryAPi,
} from "@/app/apiConfig/services/notificationsAbstractApiLayer";
import NoDataFound from "@/app/components/common/warning/NoDataFound";
import RequiredMessage from "@/app/components/webloading/errorHandling/RequiredMessage";
import SuccessMessage from "@/app/components/webloading/errorHandling/SuccessMessage";
import { t } from "i18next";

interface NotificationTabItem {
  key: string;
  label: string;
  count: number;
}

interface NotificationAlertProps {
  onClose?: () => void;
}

const NotificationAlert = ({ onClose }: NotificationAlertProps = {}) => {
  const dispatch = useDispatch();
  const handleClose = () =>
    onClose ? onClose() : dispatch(handleNotification());
  const [activeTab, setActiveTab] = useState("All");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [visibleIds, setVisibleIds] = useState<string[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [notificationTabs, setNotificationTabs] = useState<
    NotificationTabItem[]
  >([]);

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const fetchTabs = async () => {
    try {
      const response: any = await gxnotifycategoryAPi();
      const tabs: NotificationTabItem[] = [
        {
          key: "All",
          label: "All",
          count: response.data.totalCount,
        },
        ...response.data.categories.map((item: any) => ({
          key: item.categoryType,
          label: item.categoryName,
          count: item.notificationCount,
        })),
      ];
      if (response.status === 1) {
        setNotificationTabs(tabs);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchTabs();
  }, []);

  const onToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const onVisibleChange = useCallback((ids: string[]) => {
    setVisibleIds(ids);
  }, []);

  const allSelected =
    visibleIds?.length > 0 && visibleIds?.every((id) => selectedIds?.has(id));

  const onSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const allCurrentlySelected =
        visibleIds.length > 0 && visibleIds.every((id) => prev.has(id));

      return allCurrentlySelected ? new Set() : new Set(visibleIds);
    });
  }, [visibleIds]);

  const updateStatus = async (status: "READ" | "ARCHIVE" | "DELETE") => {
    if (selectedIds.size === 0) {
      setErrorMessage("Please select at least one notification.");
      setTimeout(() => {
        setErrorMessage("");
      }, 2000);
      return;
    }
    setSuccessMessage("");
    setErrorMessage("");
    const payload = {
      notificationHistIds: Array.from(selectedIds).map(Number),
      status,
    };

    try {
      const response = await gxnotifyupdateApi(payload);

      if (response.status === 1) {
        setSuccessMessage("Successfully updated.");
        setSelectedIds(new Set());
        setRefreshKey((k) => k + 1);

        fetchTabs();

        setTimeout(() => {
          setSuccessMessage("");
        }, 2000);
      } else {
        setErrorMessage("Something went wrong. Please try again.");
        setTimeout(() => {
          setErrorMessage("");
        }, 2000);
      }
    } catch (error) {
      setErrorMessage("Something went wrong. Please try again.");

      setTimeout(() => {
        setErrorMessage("");
      }, 2000);
    }
  };

  return (
    <DialogNoFooter
      close={handleClose}
      open={true}
      title=""
      subtitle="Notifications & Alerts"
    >
      <div className="notification-panel">
        {notificationTabs?.length > 0 ? (
          <>
            <NotificationTab
              notificationTabs={notificationTabs}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              unreadOnly={unreadOnly}
              onUnreadOnlyChange={setUnreadOnly}
              onMarkRead={() => updateStatus("READ")}
              onArchive={() => updateStatus("ARCHIVE")}
              onDelete={() => updateStatus("DELETE")}
              onSelectAll={onSelectAll}
              allSelected={allSelected}
            />

            {successMessage && <SuccessMessage message={successMessage} />}

            {errorMessage && <RequiredMessage message={errorMessage} />}

            {activeTab && (
              <NotificationView
                key={refreshKey}
                activeTab={activeTab}
                unreadOnly={unreadOnly}
                selectedIds={selectedIds}
                onToggleSelect={onToggleSelect}
                onVisibleChange={onVisibleChange}
              />
            )}
          </>
        ) : (
          <div>
            <NoDataFound className="gx-no-data" message={t("no_data_found")} />
          </div>
        )}
      </div>
    </DialogNoFooter>
  );
};

export default NotificationAlert;
